import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Mail, MessageCircle, Phone, Facebook, Instagram, Linkedin } from "lucide-react";
import FooterForm from "@/components/FooterForm";
import { NAV_LINKS, SERVICES, EDUANANT_URL, SLOGAN, SOCIALS } from "@/data/site";
import { CONTACT, whatsappUrl } from "@/config/site.config";
import { EDUANANT } from "@/data/eduanant";
import { asset } from "@/lib/assets";

const Footer = () => {
  const { pathname } = useLocation();
  // The Contact page already has the full enquiry wizard — showing the footer form there
  // duplicated the same ask twice on one screen.
  const showQuickForm = pathname !== "/contact";

  return (
    <footer className="relative border-t border-white/10" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-end gap-12 border-b border-white/10 py-16 md:grid-cols-2 md:py-20">
          <div>
            <img src={asset("/assets/ui/snapx-logo.webp")} alt="Snapx Technologies" width={539} height={168} loading="lazy" decoding="async" className="h-14 w-auto" data-testid="footer-logo" />
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/50">
              Snapx Technologies LLP — a product + services company from Patna. We build products, brands and businesses. Our flagship is EduAnant, school management software running live in a CBSE school of {EDUANANT.liveSchool.students} students in Patna. SNAPXOps, for tool rooms, is in early access.
            </p>
            <p className="font-serif-display mt-5 text-xl italic text-volt/80">{SLOGAN}</p>
            <a
              href={EDUANANT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-eduanant-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-white/70 transition-colors hover:text-volt"
            >
              Visit EduAnant <ArrowUpRight className="h-4 w-4" />
            </a>
            <div className="mt-8 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Snapx Technologies on ${s.name}`}
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/60 transition-colors duration-300 hover:border-volt/60 hover:text-volt"
                >
                  {s.name === "Facebook" && <Facebook className="h-4 w-4" />}
                  {s.name === "Instagram" && <Instagram className="h-4 w-4" />}
                  {s.name === "LinkedIn" && <Linkedin className="h-4 w-4" />}
                  {s.name === "WhatsApp" && <MessageCircle className="h-4 w-4" />}
                </a>
              ))}
            </div>
          </div>
          <div data-testid="footer-direct-contact">
            <p className="font-serif-display text-3xl font-light leading-tight md:text-4xl">
              Have something <span className="italic text-volt">to build?</span>
            </p>
            <p className="mt-3 text-sm text-white/50">Write to us — a senior team member replies within one working day.</p>
            <div className="mt-8 space-y-4">
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="group flex items-center gap-3 text-white transition-colors hover:text-volt">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors group-hover:border-volt/50">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-base font-bold md:text-lg">{CONTACT.email}</span>
              </a>
              <a href={`tel:${CONTACT.phoneE164.replace(/[^+\d]/g, "")}`} data-testid="footer-phone-link" className="group flex items-center gap-3 text-white transition-colors hover:text-volt">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors group-hover:border-volt/50">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="text-base font-bold md:text-lg">{CONTACT.phone}</span>
              </a>
              <a href={whatsappUrl()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="group flex items-center gap-3 text-white transition-colors hover:text-volt">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors group-hover:border-volt/50">
                  <MessageCircle className="h-4 w-4" />
                </span>
                <span className="text-base font-bold md:text-lg">Chat on WhatsApp</span>
              </a>
            </div>
          </div>
        </div>

        <div className="grid gap-14 py-16 md:grid-cols-12">
          {showQuickForm && (
            <div className="md:col-span-5">
              <p className="mb-6 text-xs uppercase tracking-[0.25em] text-white/50">Send a quick message</p>
              <FooterForm />
            </div>
          )}
          <div className={showQuickForm ? "md:col-span-3 md:col-start-7" : "md:col-span-3"}>
            <p className="text-xs uppercase tracking-[0.25em] text-white/50">Explore</p>
            <nav className="mt-5 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`footer-${l.label.toLowerCase()}-link`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/pricing" className="text-sm text-white/60 transition-colors hover:text-white" data-testid="footer-pricing-link">
                Engagement & pricing
              </Link>
              <Link to="/contact" className="text-sm text-white/60 transition-colors hover:text-white" data-testid="footer-contact-link">
                Contact
              </Link>
            </nav>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] text-white/50">Services</p>
            <nav className="mt-5 grid grid-cols-1 gap-3">
              {SERVICES.slice(0, 6).map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`footer-service-${s.slug}-link`}>
                  {s.name}
                </Link>
              ))}
              <Link to="/services" className="text-sm font-bold text-volt/80 transition-colors hover:text-volt" data-testid="footer-services-all-link">
                All 12 services →
              </Link>
            </nav>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-8 text-xs text-white/50 md:flex-row md:items-center md:justify-between">
          <p className="tracking-wide" data-testid="footer-copyright">© {new Date().getFullYear()} SNAPX TECHNOLOGIES LLP</p>
          <nav className="flex flex-wrap gap-x-6 gap-y-2" data-testid="footer-legal-links">
            <Link to="/legal/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/legal/terms" className="transition-colors hover:text-white" data-testid="footer-terms-link">Terms & Conditions</Link>
            <Link to="/legal/refunds" className="transition-colors hover:text-white" data-testid="footer-refunds-link">Refund & Cancellation</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
